import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getHeroImage } from '../utils/imageMapping';

// Create enhanced placeholder for Mobile Legends heroes
const createPlaceholderImage = (heroName, primaryRole) => {
  const roleColors = {
    tank: '#4CAF50',
    fighter: '#FF9800', 
    assassin: '#F44336',
    mage: '#9C27B0',
    marksman: '#2196F3',
    support: '#FFC107'
  };
  
  const color = roleColors[primaryRole] || '#666';
  const initials = heroName.substring(0, 2).toUpperCase();
  
  // Create a data URL for an enhanced circular placeholder
  const canvas = document.createElement('canvas');
  canvas.width = 120;
  canvas.height = 120;
  const ctx = canvas.getContext('2d');
  const centerX = 60;
  const centerY = 60;
  const radius = 58;
  
  // Create circular clipping path
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
  ctx.clip();
  
  // Create gradient background
  const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
  gradient.addColorStop(0, color);
  gradient.addColorStop(1, color + '80');
  
  // Fill background with gradient
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 120, 120);
  
  // Add circular border
  ctx.strokeStyle = color;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.arc(centerX, centerY, radius - 1.5, 0, 2 * Math.PI);
  ctx.stroke();
  
  // Add hero initials
  ctx.fillStyle = 'white';
  ctx.font = 'bold 28px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.shadowColor = 'rgba(0,0,0,0.5)';
  ctx.shadowBlur = 2;
  ctx.fillText(initials, centerX, centerY - 5);
  
  // Add role indicator
  ctx.shadowBlur = 0;
  ctx.font = '10px Arial';
  ctx.fillText(primaryRole?.toUpperCase() || 'HERO', centerX, centerY + 20);
  
  return canvas.toDataURL();
};

// Function to load image with fallback to placeholder
const loadImageWithFallback = async (url) => {
  console.log(`🔄 Attempting to load image: ${url}`);
  
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      
      // Don't set crossOrigin to avoid CORS issues with external domains
      // img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        console.log(`✅ Image loaded successfully`);
        resolve(url);
      };
      
      img.onerror = () => {
        console.warn(`❌ Image load failed`);
        reject(new Error('Image load failed'));
      };
      
      img.src = url;
    });
  } catch (error) {
    console.warn(`❌ Failed to load image:`, error.message);
    throw error;
  }
};

const ImageContainer = styled.div`
  position: relative;
  width: ${props => props.size || '100%'};
  height: ${props => props.size || '100%'};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 6px;
`;

// Heroes whose source images are already face/portrait cropped
const FACE_ONLY_HEROES = new Set(['Suyou', 'Kalea', 'Lukas', 'Zetian']);

const StyledImage = styled.img`
  position: absolute;
  object-fit: cover;
  opacity: ${props => props.$loading ? 0 : 1};
  transition: opacity 0.2s ease-in-out;
  filter: ${props => props.$grayscale ? 'grayscale(100%)' : 'none'};

  ${props => props.$faceOnly ? `
    width: 120%;
    height: 120%;
    top: -10%;
    left: -10%;
    object-fit: cover;
    object-position: center center;
  ` : `
    width: 100%;
    height: 100%;
    top: 0;
    object-position: center 15%;
  `}
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.2s ease-in-out;
  border-radius: 6px;
`;

const LoadingSpinner = styled.div`
  width: ${props => {
    const containerSize = parseInt(props.size) || 60;
    return Math.max(16, Math.min(24, containerSize * 0.4)) + 'px';
  }};
  height: ${props => {
    const containerSize = parseInt(props.size) || 60;
    return Math.max(16, Math.min(24, containerSize * 0.4)) + 'px';
  }};
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: #fff;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * HeroImage Component
 * 
 * Displays a hero image with official Mobile Legends images or enhanced placeholders
 * 
 * @param {Object} props - Component props
 * @param {Object} props.hero - Hero object with name and attributes (takes priority)
 * @param {string} props.heroName - Hero name string (fallback if hero not provided)
 * @param {string} props.size - Size of the hero image (e.g., '40px', 'small', 'medium', 'large')
 * @param {boolean} props.$grayscale - Whether to apply grayscale effect
 * @param {Function} props.onClick - Click handler
 * @param {boolean} props.selected - Whether the hero is selected
 */
const HeroImage = ({ 
  hero, 
  heroName, 
  size = 'medium', 
  $grayscale = false, 
  onClick,
  selected = false 
}) => {
  const [loading, setLoading] = useState(true);
  const [imageUrl, setImageUrl] = useState(null);
  const [loadMethod, setLoadMethod] = useState('');

  // Determine the actual hero object and name to use
  const actualHero = hero || (heroName ? { name: heroName, attributes: { primary: 'unknown' } } : null);
  const actualHeroName = actualHero?.name || heroName || '';

  // Convert size to pixel value for styling
  const getPixelSize = (sizeValue) => {
    if (typeof sizeValue === 'string' && sizeValue.endsWith('px')) {
      return sizeValue;
    }
    switch (sizeValue) {
      case 'small': return '32px';
      case 'medium': return '48px';
      case 'large': return '64px';
      default: return sizeValue || '48px';
    }
  };

  const pixelSize = getPixelSize(size);

  useEffect(() => {
    if (!actualHero || !actualHeroName) {
      setImageUrl(null);
      setLoadMethod('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadMethod('');
    
    // Get the official hero image URL
    const heroImageUrl = getHeroImage(actualHeroName);
    const primaryRole = actualHero.primary || actualHero.attributes?.primary || 'unknown';
    
    console.log(`🎯 Processing hero: ${actualHeroName}, role: ${primaryRole}`);
    console.log(`📋 Image URL: ${heroImageUrl}`);
    
    // Check if this is a real image URL (not the placeholder via.placeholder.com)
    if (heroImageUrl && !heroImageUrl.includes('via.placeholder.com')) {
      console.log(`🚀 Attempting to load official image for ${actualHeroName}`);

      // Try to load the official image
      loadImageWithFallback(heroImageUrl)
        .then(workingUrl => {
          console.log(`🎉 SUCCESS! Loaded official image for ${actualHeroName}`);
          setImageUrl(workingUrl);
          setLoading(false);
          setLoadMethod('OFFICIAL IMAGE');
        })
        .catch(error => {
          console.warn(`💥 FAILED to load official image for ${actualHeroName}:`, error.message);
          
          // Create enhanced placeholder as fallback
          const placeholderUrl = createPlaceholderImage(actualHeroName, primaryRole);
          setImageUrl(placeholderUrl);
          setLoading(false);
          setLoadMethod('PLACEHOLDER');
        });
    } else {
      // Use enhanced placeholder for heroes without mapped images
      console.log(`📝 No official image for ${actualHeroName}, using enhanced placeholder`);
      const placeholderUrl = createPlaceholderImage(actualHeroName, primaryRole);
      setImageUrl(placeholderUrl);
      setLoading(false);
      setLoadMethod('NO MAPPING');
    }
  }, [actualHero, actualHeroName]);

  if (!actualHero && !actualHeroName) {
    // Return empty placeholder
    return (
      <ImageContainer 
        size={pixelSize}
        onClick={onClick}
        style={{ 
          cursor: onClick ? 'pointer' : 'default',
          border: selected ? '2px solid #4CAF50' : 'none'
        }}
      >
        <div style={{ 
          fontSize: '12px', 
          color: '#666', 
          textAlign: 'center' 
        }}>
          +
        </div>
      </ImageContainer>
    );
  }
  
  return (
    <ImageContainer 
      size={pixelSize}
      onClick={onClick}
      style={{ 
        cursor: onClick ? 'pointer' : 'default',
        border: selected ? '2px solid #4CAF50' : 'none'
      }}
    >
      {imageUrl && (
        <StyledImage
          src={imageUrl}
          alt={actualHeroName}
          $loading={loading}
          $grayscale={$grayscale}
          $faceOnly={FACE_ONLY_HEROES.has(actualHeroName)}
          onLoad={() => {
            console.log(`🖼️ Image rendered successfully for ${actualHeroName}`);
            setLoading(false);
          }}
          onError={(e) => {
            console.error(`💥 Image rendering error for ${actualHeroName}:`, e);
            // Fallback to placeholder on render error
            const primaryRole = actualHero.primary || actualHero.attributes?.primary || 'unknown';
            const placeholderUrl = createPlaceholderImage(actualHeroName, primaryRole);
            setImageUrl(placeholderUrl);
            setLoadMethod('RENDER ERROR');
            setLoading(false);
          }}
        />
      )}
      <LoadingOverlay $visible={loading}>
        <LoadingSpinner size={pixelSize} />
      </LoadingOverlay>
    </ImageContainer>
  );
};

export default React.memo(HeroImage);
